import React, { useEffect, useState } from 'react'
import { Layer, Obj, ObjValueDictionary, ObjValueNumber, ObjValueText } from '../../services/models'
import { Button, Drawer, Form, Input, message, Select, Space, Table, InputNumber } from 'antd'
import AdminPageTitle from '../../components/AdminPageTitle/AdminPageTitle'
import objsService from '../../services/objsService'
import { useParams } from 'react-router-dom'
import layersService from '../../services/layersService'
import { Helmet } from 'react-helmet-async'

type FormData = {
  name: string;
  description: string;
  src: string;
  lat: string;
  long: string;
  attributeNumbers: { [id: string]: { value: number } };
  attributeTexts: { [id: string]: { value: string } };
  attributeDictionaries: { [id: string]: { valueId: string } };
}

type Props = {}

const AdminObjsPage: React.FC<Props> = () => {
  const { layerId } = useParams()
  if (!layerId) return <></>

  const [loading, setLoading] = useState<boolean>(false)

  const [layer, setLayer] = useState<Layer>()
  const [dataSource, setDataSource] = useState<Obj[]>([])
  const [columns, setColumns] = useState<any[]>([])

  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()

  const [editableId, setEditableId] = useState<number>(0)
  const [showCreateForm, setShowCreateForm] = useState(false)

  const onShowCreateForm = () => {
    setShowCreateForm(true)
  }
  const closeCreateForm = () => {
    setShowCreateForm(false)
  }

  const onSubmitCreateForm = (data: FormData) => {
    setLoading(true)
    const objValueNumbers: ObjValueNumber[] = []
    if (data.attributeNumbers) {
      for (const [k, v] of Object.entries(data.attributeNumbers)) {
        objValueNumbers.push({ attributeNumberId: +k, value: v.value })
      }
    }

    const objValueTexts: ObjValueText[] = []
    if (data.attributeTexts) {
      for (const [k, v] of Object.entries(data.attributeTexts)) {
        objValueTexts.push({ attributeTextId: +k, value: v.value })
      }
    }

    const objValueDictionaries: ObjValueDictionary[] = []
    if (data.attributeDictionaries) {
      for (const [k, v] of Object.entries(data.attributeDictionaries)) {
        objValueDictionaries.push({ attributeDictionaryId: +k, valueId: +v.valueId })
      }
    }

    objsService.create(data.name, +layerId, +data.long, +data.lat, objValueNumbers, objValueDictionaries, data.src, data.description, objValueTexts).then(() => {
      message.success('Успех')
      setShowCreateForm(false)
      createForm.resetFields()
      fetchData()
    }).catch((data) => {
      message.error(data.message)
    }).finally(() => {
      setLoading(false)
    })
  }

  const onCloseEditForm = () => {
    setEditableId(0)
    editForm.resetFields()
  }

  const onSubmitEditForm = (data: FormData) => {
    setLoading(true)
    const objValueNumbers: ObjValueNumber[] = []
    if (data.attributeNumbers) {
      for (const [k, v] of Object.entries(data.attributeNumbers)) {
        objValueNumbers.push({ attributeNumberId: +k, value: v.value })
      }
    }

    const objValueTexts: ObjValueText[] = []
    if (data.attributeTexts) {
      for (const [k, v] of Object.entries(data.attributeTexts)) {
        objValueTexts.push({ attributeTextId: +k, value: v.value })
      }
    }

    const objValueDictionaries: ObjValueDictionary[] = []
    if (data.attributeDictionaries) {
      for (const [k, v] of Object.entries(data.attributeDictionaries)) {
        objValueDictionaries.push({ attributeDictionaryId: +k, valueId: +v.valueId })
      }
    }

    objsService.update(editableId, data.name, +layerId, +data.long, +data.lat, objValueNumbers, objValueDictionaries, data.src, data.description, objValueTexts).then(() => {
      message.success('Успешно!')
      fetchData()
    }).catch((data) => {
      message.error(data.message)
    }).finally(() => {
      setLoading(false)
    })
    setEditableId(0)
    editForm.resetFields()
  }

  const openObj = (layer: Layer, o: Obj) => {
    if (!layer) return
    const { id, name, lat, long, numberValues, dictionaryValues, description, src, textValues } = o
    editForm.setFieldValue('id', id)
    editForm.setFieldValue('name', name)
    editForm.setFieldValue('src', src)
    editForm.setFieldValue('description', description)
    editForm.setFieldValue('lat', lat)
    editForm.setFieldValue('long', long)

    const numberValueByAttributeId: Record<number, number> = {}
    numberValues?.forEach((i) => {
      if (i.value) {
        numberValueByAttributeId[i.attributeNumberId] = i.value
      }
    })

    const textValueByAttributeId: Record<number, string> = {}
    textValues?.forEach((i) => {
      if (i.value) {
        textValueByAttributeId[i.attributeTextId] = i.value
      }
    })

    const dictionaryValueIdByAttributeId: Record<number, string> = {}
    dictionaryValues?.forEach((i) => {
      dictionaryValueIdByAttributeId[i.attributeDictionaryId] = i.valueId.toString()
    })

    layer.attributeNumbers.forEach((a) => {
      const value = numberValueByAttributeId[a.id]
      if (value) {
        editForm.setFieldValue(['attributeNumbers', a.id.toString(), 'value'], value)
      }
    })

    layer.attributeTexts.forEach((a) => {
      const value = textValueByAttributeId[a.id]
      if (value) {
        editForm.setFieldValue(['attributeTexts', a.id.toString(), 'value'], value)
      }
    })

    layer.attributeDictionaries.forEach((a) => {
      const valueId = dictionaryValueIdByAttributeId[a.id]
      editForm.setFieldValue(['attributeDictionaries', a.id.toString(), 'valueId'], valueId)
      editForm.setFieldValue('test', valueId)
    })

    setEditableId(id)
  }

  const fetchData = async () => {
    if (!layerId) return
    const getLayerResponse = await layersService.get(+layerId)
    setLayer(getLayerResponse.layer)

    objsService.get(+layerId, {})
      .then((r) => {
        const nameById: Record<number, string> = {}
        for (const a of getLayerResponse.layer.attributeDictionaries) {
          for (const v of a.values) {
            nameById[v.id] = v.name
          }
        }

        setDataSource(r.objs.map(o => {
          const numbers: { [k: string]: number } = {}
          o.numberValues.forEach((v) => {
            numbers[`number:${v.attributeNumberId}`] = v.value
          })

          const texts: { [k: string]: string } = {}
          o.textValues.forEach((v) => {
            texts[`text:${v.attributeTextId}`] = v.value
          })

          const dictionaries: { [k: string]: string } = {}
          o.dictionaryValues.forEach((v) => {
            dictionaries[`dictionary:${v.attributeDictionaryId}`] = nameById[v.valueId]
          })
          return { ...o, ...numbers, ...texts, ...dictionaries }
        }))

        const attributes: any[] = []

        for (const a of getLayerResponse.layer.attributeNumbers) {
          attributes.push({
            title: a.unit ? `${a.name} (${a.unit})` : a.name,
            dataIndex: `number:${a.id}`
          })
        }

        for (const a of getLayerResponse.layer.attributeTexts) {
          attributes.push({
            title: a.name,
            dataIndex: `text:${a.id}`
          })
        }

        for (const a of getLayerResponse.layer.attributeDictionaries) {
          attributes.push({
            title: a.name,
            dataIndex: `dictionary:${a.id}`
          })
        }

        setColumns([
          {
            title: '#',
            dataIndex: 'id',
            key: 'id'
          },
          {
            title: 'Название',
            render: (_: any, o: Obj) => <a onClick={() => openObj(getLayerResponse.layer, o)}>{o.name}</a>
          },
          ...attributes
        ])
      })
  }

  useEffect(() => {
    fetchData()
  }, [layerId])

  const renderFormItems = () => {
    if (!layer) return
    return <>
      <Form.Item label="Name" name="name" rules={[{ required: true }]}>
        <Input/>
      </Form.Item>

      <Form.Item label="Description" name="description">
        <Input.TextArea rows={4} maxLength={5000}/>
      </Form.Item>

      <Form.Item label="src" name="src">
        <Input/>
      </Form.Item>

      <Form.Item label="Lat" name="lat">
        <InputNumber<string> min="-90" max="90" step="0.000001" stringMode style={{ width: '110px' }}/>
      </Form.Item>

      <Form.Item label="Long" name="long">
        <InputNumber<string> min="-180" max="180" step="0.000001" stringMode style={{ width: '110px' }}/>
      </Form.Item>

      {
        layer.attributeNumbers.map(a => (
          <Form.Item key={a.id.toString()}
                     label={a.unit ? a.name + '(' + a.unit + ')' : a.name}
                     name={['attributeNumbers', a.id.toString(), 'value']}>
            <InputNumber
              style={{ width: 150 }}
            />
          </Form.Item>
        ))
      }
      {
        layer.attributeTexts.map(a => (
          <Form.Item key={a.id.toString()}
                     label={a.name}
                     name={['attributeTexts', a.id.toString(), 'value']}>
            <Input/>
          </Form.Item>
        ))
      }
      {
        layer.attributeDictionaries.map(a => (
          <Form.Item key={a.id.toString()}
                     label={a.name}
                     name={['attributeDictionaries', a.id.toString(), 'valueId']}>
            <Select showSearch
                    optionFilterProp="label"
                    onSearch={() => {
                    }}
                    options={a.values.map(v => ({ value: v.id.toString(), label: v.name }))}/>
          </Form.Item>
        ))
      }
    </>
  }

  return (
    <div>
      <Helmet><title>{`Prisma — Объекты: ${layer?.name}`}</title></Helmet>
      <AdminPageTitle title={`Объекты: ${layer?.name!}`}
                      buttonText={'Добавить'}
                      onClickButton={onShowCreateForm}/>
      <div style={{
        background: 'white',
        overflow: 'scroll'
      }}>
        <Table loading={loading} dataSource={dataSource} columns={columns} rowKey={'id'} tableLayout={'auto'}/>
      </div>

      <Drawer
        title="Добавить"
        width={600}
        onClose={closeCreateForm}
        open={showCreateForm}
        bodyStyle={{ paddingBottom: 80 }}
        extra={
          <Space>
            <Button onClick={closeCreateForm}>Отмена</Button>
            <Button onClick={createForm.submit} type="primary">
              Отправить
            </Button>
          </Space>
        }
      >
        <Form layout="vertical"
              form={createForm}
              onFinish={onSubmitCreateForm}>
          {renderFormItems()}
        </Form>
      </Drawer>

      <Drawer
        title="Редактирование"
        width={600}
        onClose={onCloseEditForm}
        open={!!editableId}
        bodyStyle={{ paddingBottom: 80 }}
        extra={
          <Space>
            <Button onClick={onCloseEditForm}>Отмена</Button>
            <Button onClick={editForm.submit} type="primary">
              Отправить
            </Button>
          </Space>
        }
      >
        <Form layout="vertical"
              form={editForm}
              onFinish={onSubmitEditForm}>
          {renderFormItems()}
        </Form>
      </Drawer>
    </div>
  )
}

export default AdminObjsPage
